import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button, Input, Switch } from '@nextui-org/react';
import { ArrowLeft, Users, Eye, Sparkles, Share2, Plus } from 'lucide-react';
import clubService from '../services/clubService';

const TEAL = '#3FCED2';

const FEATURES = [
  {
    icon: <Users className="w-6 h-6" />,
    title: 'VEREINSPROFIL',
    text: 'Als Vereinsbesitzer kannst du ein detailliertes Profil für deinen Dartverein erstellen. Du kannst relevante Informationen wie Name, Standort, Kontaktdaten, Spielzeiten und weitere Details angeben.',
  },
  {
    icon: <Eye className="w-6 h-6" />,
    title: 'SICHTBARKEIT ERHÖHEN',
    text: 'Durch das Vereinsprofil erhöhst du die Sichtbarkeit deines Vereins. Dartsportbegeisterte in deiner Region können deinen Verein leichter finden.',
  },
  {
    icon: <Sparkles className="w-6 h-6" />,
    title: 'MITGLIEDER GEWINNEN',
    text: 'Die App ermöglicht es potenziellen Mitgliedern, deinen Verein zu entdecken. Teile Informationen über Mitgliedschaft, Training und Veranstaltungen.',
  },
  {
    icon: <Share2 className="w-6 h-6" />,
    title: 'SOCIAL MEDIA',
    text: 'Verbinde deine Social-Media-Kanäle mit dem Vereinsprofil und stärke deine Präsenz in den sozialen Medien.',
  },
];

const CreateClubPage: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [steeldart, setSteeldart] = useState(true);
  const [edart, setEdart] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Bitte einen Namen festlegen');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await clubService.createClub({ name, steeldart, edart });
      const created = (res as any)?.data ?? res;
      const id = created?._id ?? created?.id;
      if (id) {
        navigate(`/verwaltung/verein/${id}/bearbeiten`);
      } else {
        navigate('/verwaltung?tab=clubs');
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.message || 'Verein konnte nicht erstellt werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Helmet>
        <title>Verein erstellen - DartCloud</title>
      </Helmet>

      {/* Header */}
      <div className="sticky top-0 z-10 bg-black/80 backdrop-blur border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/verwaltung?tab=clubs')} className="text-zinc-300 hover:text-white p-1">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold flex-1">Verein erstellen</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-8">
        {/* Hero */}
        <section className="text-center py-6">
          <div
            className="inline-flex w-20 h-20 rounded-full items-center justify-center mb-4"
            style={{ backgroundColor: `${TEAL}22`, color: TEAL }}
          >
            <Users size={40} />
          </div>
          <h2 className="text-2xl font-bold mb-2">Dein Dartverein in der DartCloud</h2>
          <p className="text-sm text-zinc-400 max-w-xl mx-auto">
            Erstelle ein detailliertes Vereinsprofil und mache deinen Verein für die Dart-Community sichtbar.
          </p>
        </section>

        {/* Form */}
        <section className="space-y-4">
          <Input
            label="Vereinsname"
            placeholder="z.B. DC Bullseye 99"
            value={name}
            onValueChange={(v) => { setName(v); setError(null); }}
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }}
            isRequired
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <span className="text-sm">Steeldart</span>
              <Switch isSelected={steeldart} onValueChange={setSteeldart} />
            </div>
            <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <span className="text-sm">E-Dart</span>
              <Switch isSelected={edart} onValueChange={setEdart} />
            </div>
          </div>
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl p-3 text-sm">
              {error}
            </div>
          )}
          <p className="text-xs text-zinc-500">
            Weitere Details (Adresse, Logo, Trainings, Beschreibung) kannst du nach dem Erstellen bearbeiten.
          </p>
        </section>

        {/* Features */}
        <section className="space-y-3">
          <h3 className="text-base font-semibold">Vorteile</h3>
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 flex items-start gap-4">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${TEAL}22`, color: TEAL }}
              >
                {f.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold tracking-wide mb-1">{f.title}</div>
                <p className="text-sm text-zinc-400">{f.text}</p>
              </div>
            </div>
          ))}
        </section>
      </div>

      {/* Sticky Action Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur border-t border-zinc-800 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
          <Button
            className="w-full"
            isDisabled={saving || !name.trim()}
            isLoading={saving}
            onPress={handleCreate}
            startContent={!saving ? <Plus size={16} /> : null}
            style={{ backgroundColor: TEAL, color: '#000' }}
          >
            Erstellen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateClubPage;
